import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { useOnboarding } from '../contexts/Onboarding';
import { ProgressRing } from './ProgressRing';
import { Button, IconButton, Icon } from '../components/ui';
import './onboarding-widget.scss';

// ── Icons ────────────────────────────────────────────────────────────────────

const TickIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const SparkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4L12 3z" />
  </svg>
);

// ── Motion ───────────────────────────────────────────────────────────────────
// One signature moment: the panel springs up out of the launcher. Everything
// else is a quiet CSS transition. Gated on prefers-reduced-motion throughout.

const PANEL_SPRING = { type: 'spring', stiffness: 420, damping: 34, mass: 0.8 };

// ── Item row ─────────────────────────────────────────────────────────────────

function ChecklistItem({ item, onGo, onSkip }) {
  const { done, skipped, optional, pending, label, hint } = item;

  return (
    <li className="rc-ob-item" data-done={done ? 'true' : 'false'} data-skipped={skipped ? 'true' : 'false'}>
      <button
        type="button"
        className="rc-ob-item__main"
        onClick={() => onGo(item)}
        disabled={pending}
        aria-label={pending ? `${label} — coming soon` : label}
      >
        <span className="rc-ob-item__check" aria-hidden="true">
          {done ? <TickIcon /> : null}
        </span>

        <span className="rc-ob-item__text">
          <span className="rc-ob-item__label">
            {label}
            {pending ? <span className="rc-ob-item__tag">Coming soon</span> : null}
          </span>
          <span className="rc-ob-item__hint">{hint}</span>
        </span>

        {!pending && !done ? (
          <span className="rc-ob-item__go" aria-hidden="true"><ChevronIcon /></span>
        ) : null}
      </button>

      {optional && !done ? (
        <Button
          variant="ghost"
          size="sm"
          className="rc-ob-item__skip"
          onClick={() => onSkip(item.key, !skipped)}
        >
          {skipped ? 'Undo' : 'Skip'}
        </Button>
      ) : null}
    </li>
  );
}

// ── Block ────────────────────────────────────────────────────────────────────

function ChecklistBlock({ block, expanded, onToggle, onGo, onSkip }) {
  // Same rule as the overall count: skipped and not-yet-buildable items don't
  // hold a block short of done.
  const counted = block.items.filter((i) => !i.skipped && !i.pending);
  const doneCount = counted.filter((i) => i.done).length;
  const blockDone = counted.length > 0 && doneCount === counted.length;

  return (
    <section className="rc-ob-block" data-done={blockDone ? 'true' : 'false'}>
      <button
        type="button"
        className="rc-ob-block__head"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="rc-ob-block__title">{block.title}</span>
        <span className="rc-ob-block__count">
          {blockDone ? <TickIcon /> : `${doneCount}/${counted.length}`}
        </span>
        <span className="rc-ob-block__chevron" data-open={expanded ? 'true' : 'false'} aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>

      {expanded ? (
        <div className="rc-ob-block__body">
          <p className="rc-ob-block__blurb">{block.blurb}</p>
          <ul className="rc-ob-block__items">
            {block.items.map((item) => (
              <ChecklistItem key={item.key} item={item} onGo={onGo} onSkip={onSkip} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// ── Once-per-session auto-open ───────────────────────────────────────────────
// sessionStorage so a reload doesn't re-pop it; the in-memory set covers a
// browser that blocks storage, so it still opens at most once per page life.

const AUTO_OPEN_KEY = 'rc.onboarding.autoOpened.';
const autoOpenedThisPage = new Set();

/** True exactly once per store per browser session. */
function claimSessionAutoOpen(storeId) {
  const key = AUTO_OPEN_KEY + storeId;
  if (autoOpenedThisPage.has(key)) return false;
  autoOpenedThisPage.add(key);
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
  } catch (e) {
    // Storage blocked: the in-memory set alone limits it to once per page life.
  }
  return true;
}

// ── Widget ───────────────────────────────────────────────────────────────────

export function OnboardingWidget() {
  const {
    visible, mode, blocks, done, total, requiredRemaining, complete,
    open, setOpen, skipItem, dismiss, storeId, viewerIsOwner,
  } = useOnboarding();

  const history = useHistory();
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // Open the block holding the first thing still to do, so the panel lands on
  // something actionable rather than on whatever happens to be first.
  const firstOpenBlock = useMemo(() => {
    const target = blocks.find((b) => b.items.some((i) => !i.done && !i.skipped && !i.pending));
    return target?.key || blocks[0]?.key || null;
  }, [blocks]);

  const [expanded, setExpanded] = useState(null);
  useEffect(() => { setExpanded((cur) => cur ?? firstOpenBlock); }, [firstOpenBlock]);

  // Settings is where setup belongs, so the panel opens itself on arrival —
  // once per visit, so closing it there makes it stay closed. Every other page
  // leaves it shut until the launcher is clicked.
  const onSettings = location.pathname.startsWith('/store-settings');
  const openedForSettings = useRef(false);
  useEffect(() => {
    if (!onSettings) { openedForSettings.current = false; return; }
    if (openedForSettings.current) return;
    openedForSettings.current = true;
    setOpen(true);
  }, [onSettings, setOpen]);

  // The owner's nudge: pop open once per browser session per store while any
  // essential step is left — so the first visit after being granted access (or
  // signing up) lands on the checklist. Closing it keeps it shut for the rest
  // of the session; Dismiss stops it for good (restorable from Settings).
  useEffect(() => {
    if (!visible || !viewerIsOwner || requiredRemaining === 0 || storeId == null) return;
    if (!claimSessionAutoOpen(storeId)) return;
    setOpen(true);
  }, [visible, viewerIsOwner, requiredRemaining, storeId, setOpen]);

  // Coming back from an OAuth round-trip (?setup=<key>), remember which block
  // sent them so the panel lands there — but do not open it.
  useEffect(() => {
    const key = new URLSearchParams(location.search).get('setup');
    if (!key) return;
    const block = blocks.find((b) => b.items.some((i) => i.key === key));
    if (block) setExpanded(block.key);
  }, [location.search, blocks]);

  if (!visible || blocks.length === 0) return null;
  // The launcher is a nudge, and a finished checklist has nothing left to nudge
  // about — it retires once every step is done.
  if (complete) return null;

  const goTo = (item) => {
    if (item.pending) return;
    history.push(item.to);
  };

  const headline = complete
    ? 'Your store is set up'
    : mode === 'first_run'
      ? 'Welcome — let’s set up your store'
      : 'Store setup';

  // The ring, the subline and the launcher pill all have to be read together,
  // so they all count the same way: every item that isn't skipped or pending.
  // The old subline quoted only the essential steps still to go, which left a
  // bare "4" in the ring sitting next to "7 steps to go" with no way to tell
  // that they were counting different things.
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const subline = complete
    ? 'Everything on the list is done. Nice one.'
    : requiredRemaining > 0
      ? `${done} of ${total} done · ${requiredRemaining} essential left`
      : `${done} of ${total} done · only optional steps left`;

  const panelBody = (
    <>
            <header className="rc-ob__head">
              <ProgressRing value={done} total={total} size={40}>
                {complete ? <TickIcon /> : `${pct}%`}
              </ProgressRing>

              <div className="rc-ob__headtext">
                <h2 className="rc-ob__title">{headline}</h2>
                <p className="rc-ob__sub">{subline}</p>
              </div>

              <IconButton
                variant="ghost"
                size="sm"
                className="rc-ob__close"
                label="Close setup checklist"
                onClick={() => setOpen(false)}
              >
                <Icon name="X" size="sm" />
              </IconButton>
            </header>

            <div className="rc-ob__bar" aria-hidden="true">
              {/* Custom property, not an inline style rule: the percentage is
                  live data, so it can't live in the stylesheet. Every colour and
                  dimension around it still comes from tokens. */}
              <span
                className="rc-ob__barfill"
                style={{ '--rc-ob-pct': `${pct}%` }}
              />
            </div>

            <div className="rc-ob__body">
              {blocks.map((block) => (
                <ChecklistBlock
                  key={block.key}
                  block={block}
                  expanded={expanded === block.key}
                  onToggle={() => setExpanded(expanded === block.key ? null : block.key)}
                  onGo={goTo}
                  onSkip={skipItem}
                />
              ))}
            </div>

            {/* Dismiss leads, with the reassurance underneath it: the worry a
                merchant has at this point is losing the list, so the answer
                belongs after the action, not instead of it. */}
            <footer className="rc-ob__foot">
              {/* Full-width primary: dismissing is a real decision. */}
              <Button variant="primary" size="lg" fullWidth onClick={dismiss}>
                Dismiss setup
              </Button>
              <span className="rc-ob__footnote">You can reopen this any time from Settings.</span>
            </footer>
    </>
  );

  return (
    <div className="rc-ob" data-open={open ? 'true' : 'false'}>
      <AnimatePresence>
        {open ? (
          <motion.div
            key="panel"
            className="rc-ob__panel"
            role="dialog"
            aria-label="Store setup checklist"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.96 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.97 }}
            transition={reduceMotion ? { duration: 0.12 } : PANEL_SPRING}
          >
            {panelBody}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <motion.button
        type="button"
        className="rc-ob__launcher"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={`Store setup — ${done} of ${total} complete`}
        whileHover={reduceMotion ? undefined : { y: -2 }}
        whileTap={reduceMotion ? undefined : { scale: 0.97 }}
        transition={PANEL_SPRING}
      >
        <ProgressRing value={done} total={total} size={26}>
          {complete ? <TickIcon /> : <SparkIcon />}
        </ProgressRing>
        <span className="rc-ob__launchertext">
          {complete ? 'Store set up' : 'Set up your store'}
        </span>
        <span className="rc-ob__launchercount">{done}/{total}</span>
      </motion.button>
    </div>
  );
}

export default OnboardingWidget;
